import { cn } from "@babybjorn/ui";
import { forwardRef } from "react";
import Icon from "../../../components/general/Icon";
import type { IconType } from "../../../components/general/Icon/Icon";
import type { FlagType } from "../Flag/Flag";
import Link from "../Link";
import * as s from "./Button.module.scss";

export type ButtonVariant =
	| "primary"
	| "secondary"
	| "inverted"
	| "outlined"
	| "noBg"
	| "white"
	| "special"
	| "container"
	| "no-style"
	| "disabled";

type ButtonType =
	| "standard"
	| "icon"
	| "tiny"
	| "play"
	| "input"
	| "navigation"
	| "large";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
	href?: string;
	variant?: ButtonVariant;
	buttonType?: ButtonType;
	label?: string;
	icon?: IconType | FlagType;
	iconSize?: number;
	iconLeft?: boolean;
	iconClass?: string;
	loading?: boolean;
	fullWidth?: boolean;
	type?: "button" | "submit" | "reset";
	alwaysSmall?: boolean;
	alwaysBig?: boolean;
	target?: string;
	noPaddingInline?: boolean;
	babylist?: boolean;
	disabled?: boolean;
	linkId?: string;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
	{
		variant = "primary",
		buttonType = "standard",
		label,
		icon,
		iconLeft = false,
		iconSize = 24,
		iconClass,
		loading,
		fullWidth,
		type = "button",
		href,
		alwaysSmall = false,
		alwaysBig = false,
		babylist = false,
		target,
		noPaddingInline,
		className,
		linkId,
		...props
	},
	ref,
) {
	const variantStyle:
		| Exclude<ButtonVariant, "container" | "no-style">
		| undefined =
		variant === "container" || variant === "no-style" ? undefined : variant;

	const conditionalClassNames = {
		[s[buttonType]]: variant !== "container",
		[s.tinyFilled]: variant === "secondary" && iconSize === 16,
		[s.inputWithIcon]: buttonType === "input" && icon,
		[s.iconGap]: icon,
		[s.iconLeft]: iconLeft,
		[s.fullWidth]: fullWidth,
		[s.alwaysSmall]: alwaysSmall,
		[s.alwaysBig]: alwaysBig,
		[s.noPaddingInline]: noPaddingInline,
		[s.babylist]: babylist,
		[s.disabledCursor]: props.disabled,
	};

	return href ? (
		<Link
			{...(linkId && { "data-link-id": linkId })}
			href={href}
			target={target}
			className={cn(
				s.button,
				variantStyle ? s[variantStyle] : undefined,
				conditionalClassNames,
				className,
			)}
		>
			<>
				{label && <span>{label}</span>}
				{icon && (
					<Icon
						icon={loading ? "loading" : icon}
						width={iconSize}
						loading={loading}
						color="inherit"
						className={cn(s.iconStyle, iconClass)}
					/>
				)}
			</>
		</Link>
	) : (
		<button
			{...(linkId && { "data-link-id": linkId })}
			ref={ref}
			type={type}
			className={cn(
				s.button,
				variantStyle ? s[variantStyle] : undefined,
				conditionalClassNames,
				className,
			)}
			disabled={props.disabled || loading}
			{...props}
		>
			<>
				{(buttonType === "standard" ||
					buttonType === "navigation" ||
					buttonType === "large") &&
					label && <span>{label}</span>}
				{icon && (
					<Icon
						icon={icon}
						width={iconSize}
						loading={loading}
						color="inherit"
						className={cn(s.iconStyle, iconClass)}
					/>
				)}
			</>
		</button>
	);
});
export default Button;
